// Exports the currently filtered History Log (changelog) view to CSV.
// Mirrors exportStageView: BOM-prefixed UTF-8, date-stamped filename,
// quote-escaped cells. Exports the filtered set across all pages.

const CSV_QUOTE = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

// Machine-parseable timestamp: "2026-06-26 14:30:00" (local time). Excel/Sheets
// read this as a real datetime serial (sortable, filterable, date math) AND
// auto-reformat it to the viewer's local human style on open — so one column
// serves both the analyst and the casual reader, on any locale.
const fmtIso = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// Parse a change into spreadsheet-friendly { fields, from, to }. Two shapes:
//
//   1. "Edited" / "User Updated": new_value is "Field: <old> → <new>", with
//      multiple fields joined by "; ". The arrow is INSIDE new_value.
//   2. "Status Updated": new_value is "Label: <new>" and old_value is
//      "Label: <old>" — same label on both sides, arrow is BETWEEN the fields.
//
// Everything else (Stage Advanced, Created, Note Added, Stores movements, …)
// puts a plain string in new_value, so this returns blanks and the raw
// Old/New columns carry the meaning. Multi-field edits join each part with " | ".
const splitLabelled = (s) => {
  const i = (s || "").indexOf(":");
  return i > -1 ? { label: s.slice(0, i).trim(), val: s.slice(i + 1).trim() } : { label: "", val: (s || "").trim() };
};

const parseChange = (entry) => {
  const raw = entry.new_value || "";

  // Shape 1 — arrow inside new_value (possibly several "; "-joined fields).
  if (raw.includes("→")) {
    const segments = raw.split(";").map((s) => s.trim()).filter(Boolean);
    const fields = [], froms = [], tos = [];
    for (const seg of segments) {
      const m = seg.match(/^(?:(.+?):\s*)?(.*?)\s*→\s*(.*)$/);
      if (!m) continue;
      fields.push((m[1] || "").trim());
      froms.push((m[2] || "").trim());
      tos.push((m[3] || "").trim());
    }
    if (fields.length) return { fields: fields.join(" | "), from: froms.join(" | "), to: tos.join(" | ") };
  }

  // Shape 2 — "Status Updated": "Label: old" / "Label: new" across the two fields.
  if (entry.action === "Status Updated" && raw) {
    const n = splitLabelled(raw);
    const o = splitLabelled(entry.old_value || "");
    if (n.label) return { fields: n.label, from: o.label === n.label ? o.val : (entry.old_value || ""), to: n.val };
  }

  return { fields: "", from: "", to: "" };
};

const COLUMNS = [
  // ISO datetime ("2026-06-25 15:52:42"): Excel/Sheets auto-parse it into a
  // real datetime and reformat to the local human style on open, while staying
  // unambiguous on any locale — so one column does both jobs.
  { h: "Timestamp",      a: (e) => fmtIso(e.timestamp) },
  { h: "User",           a: (e) => e.user },
  { h: "Action",         a: (e) => e.action },
  { h: "Stage",          a: (e) => e.stage },
  { h: "Client",         a: (e) => e.client },
  { h: "PO Number",      a: (e) => e.po_number },
  { h: "Line",           a: (e) => e.line_number },
  { h: "Part Number",    a: (e) => e.part_number },
  { h: "Revision",       a: (e) => e.revision },
  { h: "Description",    a: (e) => e.item_description },
  { h: "Changed Fields", a: (e) => parseChange(e).fields },
  { h: "From",           a: (e) => parseChange(e).from },
  { h: "To",             a: (e) => parseChange(e).to },
  { h: "Old Value",      a: (e) => e.old_value },
  { h: "New Value",      a: (e) => e.new_value },
  { h: "Bulk",           a: (e) => (e.is_bulk ? "Yes" : "No") },
  { h: "Bulk Count",     a: (e) => (e.is_bulk ? (e.bulk_count || 0) : "") },
];

export function exportHistoryLog(entries = []) {
  const headerRow = COLUMNS.map((c) => CSV_QUOTE(c.h)).join(",");
  const dataRows = entries.map((e) => COLUMNS.map((c) => CSV_QUOTE(c.a(e) ?? "")).join(","));
  const csv = "\uFEFF" + [headerRow, ...dataRows].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const now = new Date();
  const stamp = `${String(now.getDate()).padStart(2,"0")}-${months[now.getMonth()]}-${String(now.getFullYear()).slice(-2)}`;
  const filename = `rocklink_history_log_${stamp}.csv`;

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { rowCount: entries.length, filename };
}
